import React, { useCallback, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FiGrid, FiRefreshCw } from "react-icons/fi";
import AsyncSection from "../common/AsyncSection";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import { RoomDetailModal, DeviceDetailModal } from "./DetailModals";

/**
 * Rooms are a card grid, not a table: there are few of them, they carry an
 * explicit `order`, and their colour/icon is the content — a monochrome table
 * row would drop the one thing that identifies a room to the user.
 */
function RoomCard({ room, onClick }) {
  // `_id: null` is the synthetic "Unassigned" bucket the backend appends. It is
  // not a real room, so it never navigates to a room page.
  const isUnassigned = !room._id;

  return (
    <button
      type="button"
      onClick={isUnassigned ? onClick : () => onClick(room)}
      className="group flex flex-col items-start rounded-xl border border-slate-200 bg-white p-5 text-left transition-shadow hover:shadow-md"
    >
      <span
        className="flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold"
        style={{
          backgroundColor: room.backcolor || "#F1F5F9",
          color: room.color || "#64748B",
        }}
      >
        {isUnassigned ? "—" : (room.name || "?").charAt(0).toUpperCase()}
      </span>

      <h3
        className="mt-3 font-bold"
        style={{ color: isUnassigned ? "#64748B" : room.color || "#1E293B" }}
      >
        {isUnassigned ? "Ungrouped devices" : room.name}
      </h3>

      <p className="mt-1 text-sm text-slate-500">
        {room.totalDevices} device{room.totalDevices === 1 ? "" : "s"}
      </p>

      {isUnassigned && (
        <p className="mt-2 text-[11px] text-slate-400">
          Devices not assigned to any room
        </p>
      )}
    </button>
  );
}

export default function UserRoomsTab() {
  const { userId } = useOutletContext();
  const navigate = useNavigate();
  const basePath = `/admin/dash/users/${userId}`;

  const fetcher = useCallback(
    () => adminUserService.fetchUserRooms(userId),
    [userId]
  );

  // Room detail opens in a modal; a device inside it hands off to the device
  // modal, so you can drill in and back out without leaving the grid.
  const [detailRoom, setDetailRoom] = useState(null);
  const [detailDevice, setDetailDevice] = useState(null);

  const { data, state, error, refetch } = useAdminResource(fetcher, [userId]);
  const rooms = data || [];

  // Show the Unassigned bucket only when it actually holds something — an empty
  // one is noise on an account where every device has a room.
  const visible = rooms.filter((r) => r._id || r.totalDevices > 0);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
          Rooms
        </h2>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw size={14} className={state === "loading" ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <AsyncSection
        state={state}
        error={error}
        onRetry={() => refetch()}
        isEmpty={visible.length === 0}
        loadingMessage="Loading rooms…"
        emptyIcon={FiGrid}
        emptyTitle="No rooms yet"
        emptyMessage="This user has not created any rooms."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map((room) => (
            <RoomCard
              key={room._id || "unassigned"}
              room={room}
              onClick={
                room._id
                  ? (r) => setDetailRoom(r)
                  : () => navigate(`${basePath}/devices?roomId=unassigned`)
              }
            />
          ))}
        </div>
      </AsyncSection>

      <RoomDetailModal
        open={Boolean(detailRoom)}
        onClose={() => setDetailRoom(null)}
        userId={userId}
        room={detailRoom}
        onOpenDevice={(d) => { setDetailRoom(null); setDetailDevice(d); }}
      />

      <DeviceDetailModal
        open={Boolean(detailDevice)}
        onClose={() => setDetailDevice(null)}
        userId={userId}
        device={detailDevice}
        onOpenRoom={(room) => { setDetailDevice(null); setDetailRoom(room); }}
      />
    </div>
  );
}
